import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Link as LinkIcon, RefreshCw, Copy, Check, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';

const AdminBrandLinksPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/shop/_admin/list`, { params: { key } });
      // Build the URL on the client using the actual host the admin is browsing from.
      // This is more reliable than relying on the backend's view of the host header.
      const origin = window.location.origin;
      const items = (res.data.items || []).map((it) => ({
        ...it,
        shop_url: `${origin}/shop/${it.slug}`,
      }));
      setItems(items);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(err?.response?.status === 403 ? 'Onjuist wachtwoord — probeer opnieuw.' : 'Kan de lijst niet ophalen.');
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authKey) fetchList(authKey); }, [authKey, fetchList]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };

  const handleCopy = async (url, brand) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(brand);
      setTimeout(() => setCopied(null), 2000);
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY); setAuthKey(''); setKeyInput(''); setItems([]);
  };

  const filtered = filter
    ? items.filter((i) => i.brand.toLowerCase().includes(filter.toLowerCase()))
    : items;

  if (!authKey) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="brand-links-admin-login">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 p-3 rounded-full mb-4">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Beheer — Merk-links</h1>
            <p className="text-sm text-gray-500 text-center">
              Voer je beheerderswachtwoord in om de Pinterest-klare links per merk te zien.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Wachtwoord"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="brand-links-key-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !keyInput.trim()}
              className="w-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-60"
            >
              {loading ? 'Bezig...' : 'Inloggen'}
            </button>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="brand-links-admin-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Merk-links voor social sharing</h1>
            <p className="text-sm text-gray-500 mt-1">
              Kopieer de link en plak hem onder je Pinterest-pin (of Instagram-link). Bezoekers worden naadloos doorverwezen naar het officiële merk.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchList(authKey)}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Vernieuwen
            </button>
            <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-700 px-3">Uitloggen</button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-sm text-blue-900">
          <p>
            <strong>Hoe werkt het?</strong> Wanneer iemand op een merk-link klikt vanaf Pinterest, Instagram of een mail,
            wordt de bezoeker via uandfashion.eu <strong>onmiddellijk doorgestuurd</strong> naar de officiële merksite — met behoud
            van je affiliate-tracking. Geen tussenpagina, geen vertraging.
          </p>
        </div>

        <input
          type="text"
          placeholder="Zoek merk…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-4 py-2.5 mb-5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          data-testid="brand-links-search"
        />

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="p-12 text-center text-gray-500">Lijst aan het laden…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              {filter ? `Geen merken gevonden voor "${filter}".` : 'Nog geen merken in de database.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((item) => (
                <li key={item.brand} className="px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`brand-link-row-${item.slug}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{item.brand}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <LinkIcon className="w-3 h-3 flex-shrink-0" />
                        <code className="truncate font-mono">{item.shop_url}</code>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleCopy(item.shop_url, item.brand)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          copied === item.brand
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                        data-testid={`brand-link-copy-${item.slug}`}
                      >
                        {copied === item.brand ? (<><Check className="w-3 h-3" /> Gekopieerd</>) : (<><Copy className="w-3 h-3" /> Kopieer</>)}
                      </button>
                      <a
                        href={item.shop_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50"
                        title="Test de redirect"
                      >
                        <ExternalLink className="w-3 h-3" /> Test
                      </a>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="text-xs text-gray-400 mt-4 text-center">{filtered.length} van {items.length} merken getoond.</p>
      </div>
    </div>
  );
};

export default AdminBrandLinksPage;
